import { AutomaticLayout, ManualLayout } from '@src/pages/private/ActionPaths/View/layouts';
import { HeaderLayout } from '@src/pages/private/ActionPaths/View/layouts/Header';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { Styles } from './includes/styles';

/**
 * Page: View
 *
 * Provides the user with a screen to view information about the action path.
 *
 * @returns {Element}
 * @constructor
 */
export function View() {
  const router = useRouter();
  const routeParams = router?.getRouteParams();

  const [hasCipSource, setHasCipSource] = useState(false);

  const { toast } = useToast();

  const { assets, isLoadingAssets } = useAssets([
    {
      apiKey: 'ActionPath',
      payload: { actionPathId: routeParams?.id },
      queryKey: 'GetActionPath',
    },
    {
      apiKey: 'ActionPath',
      payload: { actionPathId: routeParams?.id },
      queryKey: 'GetActionPathStatistics',
    },
  ]);

  const actionPath = assets?.GetActionPath?.data;
  const actionPathStatistics = assets?.GetActionPathStatistics?.data;

  /**
   * Redirect the user back to the list screen if action path status is not "ACTIVE"
   */
  useLayoutEffect(() => {
    if (!isEmpty(actionPath) && actionPath?.status !== 'ACTIVE') {
      router?.navigate('/action-paths');
      toast.show({
        id: 'action-path-status-error',
        message: (
          <React.Fragment>
            Not allowed to view action path with status other than <code>active</code>.
          </React.Fragment>
        ),
        title: 'Invalid Status',
        variant: 'error',
      });
    }
  }, [actionPath]);

  /**
   * Determine if there are any action assignments with an impacted source of CIP.
   */
  useEffect(() => {
    if (!isEmpty(actionPath)) {
      const hasCip = actionPath?.assignments?.some((assignment) => {
        return assignment?.impactedSource === 'CIP';
      });

      if (hasCip !== hasCipSource) {
        setHasCipSource(hasCip);
      }
    }
  }, [actionPath]);

  return (
    <ErrorHandler location="src/pages/private/ActionPaths/View/View.jsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Styles>
          <HeaderLayout
            actionPath={actionPath}
            actionPathStatistics={actionPathStatistics}
            isLoading={isLoadingAssets}
          />

          {actionPath?.manualAssociation === true ? (
            <ManualLayout
              actionPath={actionPath}
              actionPathStatistics={actionPathStatistics}
              hasCipSource={hasCipSource}
              isLoading={isLoadingAssets}
            />
          ) : (
            <AutomaticLayout
              actionPath={actionPath}
              actionPathStatistics={actionPathStatistics}
              hasCipSource={hasCipSource}
              isLoading={isLoadingAssets}
            />
          )}
        </Styles>
      </motion.div>
    </ErrorHandler>
  );
}
